import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first, then browser language, default to French
    const saved = localStorage.getItem('oceana-language');
    if (saved) return saved;
    
    const browserLang = navigator.language.slice(0, 2);
    return browserLang === 'en' ? 'en' : 'fr';
  });

  useEffect(() => {
    localStorage.setItem('oceana-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to French if key not found
        let fallback = translations['fr'];
        for (const fk of keys) {
          if (fallback && fallback[fk]) {
            fallback = fallback[fk];
          } else {
            return key; // Return key if not found
          }
        }
        return fallback;
      }
    }
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translations object
const translations = {
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      distribution: 'Distribution B2B',
      production: 'Production',
      boutiques: "Fraîcheurs d'Océana",
      services: 'Services Entreprises',
      catalogue: 'Catalogue',
      about: 'À propos',
      contact: 'Contact',
      order: 'Commander',
      proSpace: 'Espace Pro',
      becomeClient: 'Devenir client',
      investors: 'Investisseurs'
    },
    
    // Common
    common: {
      learnMore: 'En savoir plus',
      contact: 'Nous contacter',
      send: 'Envoyer',
      sending: 'Envoi en cours...',
      phone: 'Téléphone',
      email: 'Email',
      address: 'Adresse',
      hours: 'Horaires',
      name: 'Nom',
      company: 'Entreprise',
      message: 'Message',
      submit: 'Envoyer',
      required: 'Requis',
      optional: 'Optionnel',
      seeMore: 'Voir plus',
      close: 'Fermer'
    },

    // Home page
    home: {
      hero: {
        title: 'Qualité, fraîcheur et fiabilité pour vos approvisionnements',
        subtitle: 'Océana accompagne les professionnels et particuliers avec une gamme premium de fruits et légumes, un service de livraison rigoureux et des solutions adaptées à vos besoins.',
        ctaB2B: 'Devenir client B2B',
        ctaOrder: 'Commander'
      },
      stats: {
        clients: 'Clients satisfaits',
        experience: "D'expérience",
        products: 'Produits premium',
        satisfaction: 'Taux de satisfaction'
      },
      trust: {
        title: 'Ils nous font confiance',
        subtitle: 'Fournisseur de confiance pour les plus grandes enseignes de distribution en Côte d\'Ivoire'
      },
      features: {
        freshness: 'Fraîcheur garantie',
        freshnessDesc: 'Chaîne du froid maîtrisée et contrôles qualité rigoureux',
        delivery: 'Livraison fiable',
        deliveryDesc: 'Logistique professionnelle et respect des horaires',
        sourcing: 'Sourcing premium',
        sourcingDesc: 'Sélection rigoureuse des meilleurs producteurs locaux et importés',
        traceability: 'Traçabilité complète',
        traceabilityDesc: 'Suivi transparent de l\'origine à la livraison'
      },
      boutique: {
        title: "Fraîcheurs d'Océana",
        subtitle: 'Nos boutiques primeur offrent une expérience d\'achat premium avec une sélection quotidienne de fruits et légumes de première qualité.',
        feature1: 'Jus frais pressés sur place',
        feature2: 'Corbeilles personnalisées',
        feature3: 'Service de pick-up rapide',
        feature4: 'Plateaux pour réunions',
        cta: 'Découvrir nos boutiques'
      },
      b2b: {
        title: 'Distribution professionnelle B2B',
        subtitle: 'Océana Distribution fournit hôtels, restaurants, traiteurs et entreprises avec des produits premium et un service sur mesure.',
        feature1: 'Compte client dédié avec tarifs préférentiels',
        feature2: 'Livraisons régulières programmées',
        feature3: 'Large gamme adaptée aux professionnels',
        feature4: 'Facturation mensuelle et suivi personnalisé',
        cta: 'Demander un devis'
      },
      testimonials: {
        title: 'Ce que disent nos clients',
        subtitle: 'La satisfaction de nos clients est notre priorité'
      },
      faq: {
        title: 'Questions fréquentes',
        subtitle: 'Trouvez rapidement les réponses à vos questions'
      },
      cta: {
        title: 'Prêt à commencer ?',
        subtitle: 'Contactez-nous pour ouvrir un compte professionnel ou passer votre première commande.',
        contact: 'Nous contacter'
      }
    },

    // Production page
    production: {
      hero: {
        badge: 'Océana Production S.A.',
        title: 'Production',
        subtitle: 'Des fermes propres aux réseaux nationaux de producteurs',
        tagline: 'Construire un écosystème agricole structuré à travers la Côte d\'Ivoire',
        description: 'Océana n\'est pas seulement un producteur — nous sommes une plateforme agricole entièrement intégrée combinant production propre et réseaux de producteurs organisés.',
        ctaOwned: 'Production propre',
        ctaNetwork: 'Réseau de producteurs'
      },
      keyMessage: {
        equation: 'Océana = Production propre + Réseau national de producteurs organisés',
        clarification: 'Pas seulement une ferme. Pas seulement un négociant. Une plateforme agricole structurée.'
      },
      owned: {
        badge: 'Production Propre',
        title: 'Site d\'Azaguié',
        description: 'Océana développe son propre site de production situé à Azaguié, qui servira de base agricole centrale pour le groupe. Ce projet représente notre engagement envers une agriculture de qualité supérieure.',
        controlled: 'Production contrôlée',
        innovations: 'Innovations pilotes',
        benchmark: 'Référence qualité',
        functionsTitle: 'Ce site fonctionne comme :',
        demo: 'Ferme de démonstration',
        demoDesc: 'Vitrine des meilleures pratiques agricoles',
        rnd: 'Plateforme R&D',
        rndDesc: 'Serres, irrigation, optimisation des rendements',
        anchor: 'Ancrage d\'approvisionnement',
        anchorDesc: 'Source fiable pour notre réseau de distribution'
      },
      distributed: {
        badge: 'Production Distribuée',
        title: 'Océana Food Hubs',
        subtitle: 'Un réseau national de producteurs structurés',
        description: 'Au-delà de notre ferme propre, Océana développe un réseau de clusters agricoles régionaux à travers des partenariats avec des coopératives et des petits producteurs.',
        hubNorth: 'Hub Nord',
        hubNorthLocation: 'Région de Korhogo',
        hubFuture: 'Futurs Hubs',
        hubFutureLocation: 'Expansion nationale',
        active: 'Actif',
        planned: 'En développement',
        philosophy: 'Notre philosophie',
        philosophyText1: 'Océana ne fait pas que cultiver la terre.',
        philosophyText2: 'Océana structure la production.',
        organizedProducers: 'Producteurs organisés',
        organizedProducersDesc: 'Coopératives et petits exploitants encadrés',
        centralPlanning: 'Planification centralisée',
        centralPlanningDesc: 'Coordination des cultures et volumes',
        qualityStandards: 'Standards qualité',
        qualityStandardsDesc: 'Normes Océana appliquées partout',
        aggregation: 'Agrégation',
        aggregationDesc: 'Infrastructure de collecte et stockage'
      },
      howItWorks: {
        title: 'Comment fonctionne le modèle',
        subtitle: 'Une chaîne de valeur intégrée, de la ferme au consommateur',
        step1: 'Producteurs',
        step1Desc: 'Production sous contrat avec accompagnement technique',
        step2: 'Production',
        step2Desc: 'Cultures guidées par les standards Océana',
        step3: 'Agrégation',
        step3Desc: 'Collecte et traitement dans les hubs régionaux',
        step4: 'Distribution',
        step4Desc: 'Acheminement vers Abidjan et au-delà',
        forProducers: 'Pour les producteurs',
        forProducersFeature1: 'Contrats de production sécurisés',
        forProducersFeature2: 'Accompagnement technique et formation',
        forProducersFeature3: 'Accès aux intrants et semences de qualité',
        forClients: 'Pour les clients',
        forClientsFeature1: 'Approvisionnement fiable et régulier',
        forClientsFeature2: 'Traçabilité complète des produits',
        forClientsFeature3: 'Qualité standardisée et constante'
      },
      infrastructure: {
        badge: 'Infrastructure clé',
        title: 'Pack House de Sinématiali',
        description: 'Un composant central de notre modèle est le pack house situé à Sinématiali, desservant le hub Nord. Cette infrastructure transforme une production dispersée en une chaîne d\'approvisionnement fiable.',
        collection: 'Collecte',
        sorting: 'Tri & Calibrage',
        packaging: 'Conditionnement',
        logistics: 'Consolidation logistique',
        quote: 'Cette infrastructure transforme une production dispersée en une chaîne d\'approvisionnement fiable et efficace.'
      },
      scale: {
        title: 'Développer le réseau',
        subtitle: 'Notre vision pour l\'agriculture ivoirienne',
        phase1: 'Phase 1',
        phase1Title: 'Hub Nord (Korhogo)',
        phase1Desc: 'Opérationnel. Premier cluster agricole structuré dans la région de Korhogo.',
        phase2: 'Phase 2',
        phase2Title: 'Expansion régionale',
        phase2Desc: 'En cours. Extension vers de nouvelles régions productrices.',
        phase3: 'Phase 3',
        phase3Title: 'Réseau national',
        phase3Desc: 'Vision. Couverture nationale des Océana Food Hubs.',
        closingLine: 'D\'une seule ferme à une plateforme agricole nationale.'
      },
      contact: {
        title: 'Rejoignez l\'écosystème Océana',
        subtitle: 'Que vous soyez producteur, coopérative, distributeur ou investisseur, découvrez comment collaborer avec notre plateforme agricole intégrée.',
        producers: 'Producteurs & Coopératives',
        producersDesc: 'Rejoignez notre réseau de production structurée',
        distributors: 'Distributeurs & GMS',
        distributorsDesc: 'Accédez à un approvisionnement fiable et traçable',
        investors: 'Investisseurs & Partenaires',
        investorsDesc: 'Participez au développement de l\'agriculture ivoirienne',
        formTitle: 'Nous contacter',
        organization: 'Organisation',
        interest: 'Intérêt',
        interestProducer: 'Producteur / Coopérative',
        interestDistributor: 'Distributeur / GMS',
        interestInvestor: 'Investisseur / Partenaire',
        interestOther: 'Autre'
      },
      cta: {
        title: 'Découvrez l\'ensemble de nos activités',
        subtitle: 'Production, distribution et boutiques — Océana couvre l\'ensemble de la chaîne de valeur agricole.',
        distribution: 'Distribution B2B',
        boutiques: 'Nos boutiques'
      }
    },

    // Contact page
    contact: {
      title: 'Contactez-nous',
      subtitle: 'Notre équipe est à votre disposition pour répondre à vos questions',
      form: {
        name: 'Nom complet',
        email: 'Email',
        phone: 'Téléphone',
        subject: 'Sujet',
        subjectOptions: {
          b2b: 'Demande B2B / Compte professionnel',
          order: 'Commande boutique',
          service: 'Services entreprises',
          info: 'Information générale',
          other: 'Autre'
        },
        message: 'Votre message',
        send: 'Envoyer le message',
        success: 'Message envoyé',
        successDesc: 'Nous vous répondrons dans les plus brefs délais.'
      },
      info: {
        address: 'Notre adresse',
        hours: 'Horaires d\'ouverture',
        weekdays: 'Lundi - Vendredi',
        saturday: 'Samedi',
        sunday: 'Dimanche',
        closed: 'Fermé'
      }
    },

    // Footer
    footer: {
      description: 'Votre partenaire de confiance pour l\'approvisionnement en fruits et légumes premium en Côte d\'Ivoire.',
      quickLinks: 'Liens rapides',
      contact: 'Contact',
      paymentMethods: 'Moyens de paiement',
      copyright: 'Tous droits réservés.',
      legal: 'Mentions légales',
      privacy: 'Politique de confidentialité'
    },

    // Promo banner
    promo: {
      offer: 'Offre spéciale B2B :',
      discount: '-10% sur votre première commande avec le code'
    },

    // Trust badges
    badges: {
      quality: 'Qualité garantie',
      delivery: 'Livraison rapide',
      service: 'Service 7j/7',
      fresh: 'Produits frais',
      experience: "10 ans d'expérience",
      satisfaction: '98% satisfaits'
    },

    // Distribution page
    distribution: {
      hero: {
        title: 'Distribution professionnelle B2B',
        subtitle: 'Océana Distribution est votre partenaire fiable pour l\'approvisionnement en fruits et légumes premium. Nous servons les hôtels, restaurants, traiteurs, bureaux et résidences avec rigueur et régularité.'
      },
      clients: {
        title: 'Nos clients de référence',
        subtitle: 'Nous approvisionnons les plus grandes enseignes de distribution en Côte d\'Ivoire'
      },
      whoWeServe: {
        title: 'Qui nous servons',
        hotels: 'Hôtels',
        hotelsDesc: 'Approvisionnement quotidien pour vos cuisines et buffets',
        restaurants: 'Restaurants',
        restaurantsDesc: 'Produits frais et de saison pour vos cartes',
        companies: 'Entreprises',
        companiesDesc: 'Corbeilles et services pour vos bureaux',
        caterers: 'Traiteurs',
        caterersDesc: 'Volume et qualité pour vos événements'
      },
      howItWorks: {
        title: 'Comment ça marche',
        step1: 'Demande de devis',
        step1Desc: 'Remplissez le formulaire ou contactez-nous directement pour nous présenter vos besoins',
        step2: 'Ouverture de compte',
        step2Desc: 'Nous établissons votre compte client avec tarifs préférentiels et conditions adaptées',
        step3: 'Livraison régulière',
        step3Desc: 'Commandes faciles et livraisons ponctuelles selon votre planning'
      },
      products: {
        title: 'Notre gamme professionnelle'
      },
      quality: {
        title: 'Notre engagement qualité',
        coldChain: 'Chaîne du froid',
        coldChainDesc: 'Camions réfrigérés et entrepôts climatés pour une fraîcheur optimale',
        controls: 'Contrôles qualité',
        controlsDesc: 'Tri et contrôle visuel systématique avant chaque livraison',
        traceability: 'Traçabilité',
        traceabilityDesc: 'Suivi complet de l\'origine à la livraison avec documentation'
      },
      farm: {
        badge: 'Océana Production',
        title: 'De la ferme à l\'assiette',
        tagline: 'Cultivé ici, livré demain.',
        description: 'Océana Production développe un projet de 30 000 m² de serres hydroponiques à Azaguié pour approvisionner les grandes surfaces d\'Abidjan en légumes frais, traçables et produits localement.',
        stats: {
          greenhouses: 'de serres prévues',
          tons: 'tonnes/an (objectif)',
          families: 'familles de produits',
          pesticides: 'pesticides',
          delivery: 'livraison J+1'
        },
        productsTitle: 'Nos futurs produits',
        productsSubtitle: '5 familles de légumes premium prévues, qui seront cultivés sous serre et récoltés à maturité',
        tomatoes: 'Tomates',
        tomatoesDesc: 'Cerise & rondes',
        cucumbers: 'Concombres',
        cucumbersDesc: 'Frais et croquants',
        peppers: 'Poivrons',
        peppersDesc: 'Rouge, jaune & vert',
        lettuce: 'Laitues',
        lettuceDesc: 'Feuilles fraîches',
        herbs: 'Herbes fraîches',
        herbsDesc: 'Basilic, menthe...',
        differenceTitle: 'Notre différence',
        betterTaste: 'Meilleur goût, meilleure durée de vie',
        betterTasteDesc: 'Contrairement aux produits importés du Maroc ou d\'Europe, nos légumes sont récoltés à maturité, conditionnés le jour même et livrés sous chaîne du froid directement aux rayons frais.',
        harvest: 'Récolte à maturité optimale',
        packaging: 'Conditionnement le jour même',
        coldChainGuaranteed: 'Chaîne du froid garantie',
        traceabilityFull: 'Traçabilité du plant à l\'emballage',
        partnerCta: 'Devenez partenaire distributeur',
        partnerDesc: 'Intégrez nos légumes locaux et premium dans vos rayons frais. Nous accompagnons les GMS, grossistes et distributeurs.',
        partnerBtn: 'Devenir partenaire distributeur',
        learnMore: 'En savoir plus sur le projet'
      },
      form: {
        title: 'Ouvrir un compte professionnel',
        subtitle: 'Remplissez ce formulaire et nous vous contacterons sous 24h pour établir votre compte client.',
        paymentTitle: 'Modes de paiement acceptés',
        paymentNote: 'Facturation mensuelle • Conditions de paiement flexibles',
        companyName: 'Nom de l\'entreprise',
        contactName: 'Nom du contact',
        email: 'Email',
        phone: 'Téléphone',
        businessType: 'Type d\'activité',
        selectOption: 'Sélectionnez...',
        hotel: 'Hôtel',
        restaurant: 'Restaurant',
        caterer: 'Traiteur',
        company: 'Entreprise',
        residence: 'Résidence',
        other: 'Autre',
        volumeTitle: 'Besoins et volumes estimés',
        volumeOptional: 'Volume estimé (optionnel)',
        volumeSmall: 'Petit (moins de 50kg/semaine)',
        volumeMedium: 'Moyen (50-200kg/semaine)',
        volumeLarge: 'Grand (200-500kg/semaine)',
        volumeVeryLarge: 'Très grand (plus de 500kg/semaine)',
        messagePlaceholder: 'Décrivez vos besoins spécifiques...',
        submit: 'Envoyer la demande',
        sending: 'Envoi en cours...',
        success: 'Demande envoyée ✓',
        successDesc: 'Nous vous contacterons sous 24h pour discuter de vos besoins.',
        error: 'Erreur',
        errorDesc: 'Une erreur est survenue. Veuillez réessayer.',
        whatsappNote: 'Ou contactez-nous directement sur'
      }
    },

    // Boutiques page
    boutiques: {
      hero: {
        title: "Fraîcheurs d'Océana",
        subtitle: "Nos boutiques primeur offrent une expérience d'achat premium avec une sélection quotidienne de fruits et légumes de première qualité, des jus frais pressés et des services personnalisés."
      },
      experience: {
        title: "Une expérience d'achat premium",
        subtitle: "Chaque matin, nos équipes sélectionnent les meilleurs produits pour vous offrir fraîcheur et qualité exceptionnelles.",
        dailySelection: 'Sélection quotidienne',
        dailySelectionDesc: 'Produits frais renouvelés chaque jour',
        personalAdvice: 'Conseil personnalisé',
        personalAdviceDesc: 'Équipe expérimentée à votre écoute',
        modernAmbiance: 'Ambiance moderne',
        modernAmbianceDesc: 'Espaces propres et bien organisés'
      },
      services: {
        title: 'Nos services',
        fruitBar: 'Fruit Bar & Jus',
        fruitBarDesc: 'Jus 100% naturels pressés à la demande. Oranges, ananas, pastèque, carotte, et mélanges personnalisés.',
        baskets: 'Corbeilles personnalisées',
        basketsDesc: 'Composition sur mesure pour vos bureaux, cadeaux d\'entreprise ou événements.',
        platters: 'Plateaux meeting',
        plattersDesc: 'Fruits découpés et présentés pour vos réunions et pauses gourmandes.',
        pickup: 'Service pick-up',
        pickupDesc: 'Commandez par WhatsApp et récupérez votre commande prête en boutique.'
      },
      locations: {
        title: 'Nos boutiques',
        servicesAvailable: 'Services disponibles :'
      },
      cta: {
        title: 'Commander ou réserver',
        subtitle: 'Contactez-nous sur WhatsApp pour passer commande, réserver vos jus ou commander vos corbeilles.',
        button: 'Commander sur WhatsApp'
      }
    },

    // Services page
    services: {
      hero: {
        title: 'Services Entreprises',
        subtitle: 'Des solutions adaptées pour améliorer le bien-être de vos équipes et la qualité de vos réunions. Livraison régulière ou ponctuelle selon vos besoins.'
      },
      whyChoose: {
        title: 'Pourquoi choisir nos services',
        flexibility: 'Flexibilité',
        flexibilityDesc: 'Livraisons ponctuelles ou récurrentes, adaptées à votre calendrier',
        quality: 'Qualité constante',
        qualityDesc: 'Sélection rigoureuse et présentation soignée à chaque livraison',
        personalized: 'Service personnalisé',
        personalizedDesc: 'Compositions sur mesure selon vos préférences et contraintes'
      },
      howToOrder: {
        title: 'Comment commander',
        subtitle: 'Contactez-nous pour discuter de vos besoins. Nous établirons ensemble une offre personnalisée avec tarifs et planning de livraison.',
        whatsapp: 'Commander sur WhatsApp',
        email: 'Envoyer un email'
      },
      testimonials: {
        title: 'Ce que disent nos clients',
        testimonial1: 'Les corbeilles de fruits hebdomadaires sont devenues un rendez-vous attendu par toute l\'équipe. Qualité et fraîcheur au top.',
        testimonial1Role: 'Office Manager',
        testimonial2: 'Service professionnel et ponctuel. Les plateaux pour nos réunions sont toujours présentés avec soin.',
        testimonial2Role: 'Responsable F&B'
      }
    },

    // Catalogue page
    catalogue: {
      hero: {
        title: 'Notre catalogue',
        subtitle: 'Une sélection variée de fruits et légumes frais, locaux et importés. La disponibilité varie selon les saisons et les arrivages quotidiens.'
      },
      categories: {
        title: 'Familles de produits',
        askList: 'Demander la liste du jour'
      },
      availability: {
        title: 'Disponibilité et fraîcheur',
        subtitle: 'Notre catalogue évolue quotidiennement en fonction des arrivages et de la saisonnalité. Pour connaître la liste complète et les prix du jour, contactez-nous directement.',
        local: 'Produits locaux',
        localDesc: 'Sourcing direct auprès de producteurs ivoiriens sélectionnés',
        imported: 'Produits importés',
        importedDesc: 'Sélection internationale via réseau d\'importateurs de confiance',
        premium: 'Produits premium',
        premiumDesc: 'Pièces d\'exception et variétés rares selon disponibilité'
      },
      cta: {
        title: 'Besoin d\'informations ?',
        subtitle: 'Contactez-nous pour recevoir la liste complète des produits disponibles, les prix et passer commande.',
        button: 'Contacter sur WhatsApp'
      }
    },

    // About page
    about: {
      hero: {
        title: 'À propos d\'Océana',
        subtitle: 'Acteur premium de la chaîne fruits & légumes à Abidjan, conçu pour servir professionnels et particuliers avec rigueur et qualité.'
      },
      history: {
        title: 'Notre histoire',
        paragraph1: 'Océana est né de la conviction qu\'il était possible de concilier qualité premium, service rigoureux et accessibilité dans le secteur des fruits et légumes à Abidjan.',
        paragraph2: 'Nous avons construit notre réputation sur la fiabilité de nos approvisionnements B2B et la fraîcheur de nos boutiques primeur. Aujourd\'hui, nous servons les plus grands hôtels, restaurants et entreprises de la place, tout en accueillant une clientèle exigeante dans nos boutiques "Fraîcheurs d\'Océana".'
      },
      mission: {
        title: 'Mission & Valeurs',
        excellence: 'Excellence',
        excellenceDesc: 'Rigueur dans la sélection, le stockage et la livraison de chaque produit',
        reliability: 'Fiabilité',
        reliabilityDesc: 'Engagement respecté, horaires tenus, qualité constante',
        passion: 'Passion',
        passionDesc: 'Amour des bons produits et satisfaction de nos clients'
      },
      coverage: {
        title: 'Zones couvertes',
        subtitle: 'Nous livrons dans toute la zone du Grand Abidjan avec une logistique optimisée pour garantir fraîcheur et ponctualité.',
        zone1: 'Cocody, Plateau, Marcory',
        zone2: 'Yopougon, Adjamé, Treichville',
        zone3: 'Bingerville, Abobo, Port-Bouët'
      },
      sourcing: {
        title: 'Sourcing',
        subtitle: 'Notre double approche locale et internationale nous permet d\'offrir qualité et diversité.',
        local: 'Producteurs locaux',
        localDesc: 'Partenariats directs avec des producteurs ivoiriens sélectionnés pour leur savoir-faire',
        network: 'Réseau d\'importateurs',
        networkDesc: 'Approvisionnements internationaux via des canaux fiables et certifiés'
      },
      commitments: {
        title: 'Nos engagements opérationnels',
        coldChain: 'Chaîne du froid',
        coldChainDesc: 'Camions réfrigérés et entrepôts climatés',
        traceability: 'Traçabilité',
        traceabilityDesc: 'Suivi complet de l\'origine à la livraison',
        qualityControl: 'Contrôle qualité',
        qualityControlDesc: 'Tri et contrôle visuel systématique',
        reliableDelivery: 'Livraison fiable',
        reliableDeliveryDesc: 'Respect des horaires et engagement tenu'
      }
    },

    // Become client page
    becomeClient: {
      hero: {
        title: 'Devenir client Océana',
        subtitle: 'Que vous soyez professionnel ou particulier, Océana vous accompagne avec des solutions adaptées à vos besoins.'
      },
      professionals: {
        title: 'Professionnels',
        subtitle: 'Vous êtes un hôtel, restaurant, traiteur, entreprise ou résidence ? Ouvrez votre compte professionnel.',
        feature1: 'Compte client dédié avec tarifs préférentiels',
        feature2: 'Livraisons régulières programmées',
        feature3: 'Large gamme adaptée aux professionnels',
        feature4: 'Facturation mensuelle personnalisée',
        feature5: 'Service client dédié',
        ctaPrimary: 'Ouvrir un compte pro',
        ctaSecondary: 'Contacter sur WhatsApp'
      },
      individuals: {
        title: 'Particuliers',
        subtitle: 'Découvrez nos boutiques "Fraîcheurs d\'Océana" pour une expérience d\'achat premium.',
        feature1: 'Sélection quotidienne de produits frais',
        feature2: 'Jus frais pressés sur place',
        feature3: 'Corbeilles personnalisées',
        feature4: 'Service pick-up rapide',
        feature5: 'Commande via WhatsApp',
        ctaPrimary: 'Découvrir nos boutiques',
        ctaSecondary: 'Commander sur WhatsApp'
      },
      enterpriseServices: {
        title: 'Services entreprises',
        subtitle: 'Vous êtes une entreprise et souhaitez offrir des corbeilles de fruits à vos équipes ou des plateaux pour vos réunions ?',
        cta: 'Découvrir nos services'
      },
      whyChoose: {
        title: 'Pourquoi choisir Océana',
        premiumQuality: 'Qualité premium',
        premiumQualityDesc: 'Sélection rigoureuse des meilleurs produits locaux et importés',
        freshness: 'Fraîcheur garantie',
        freshnessDesc: 'Chaîne du froid maîtrisée et contrôles qualité rigoureux',
        reliableService: 'Service fiable',
        reliableServiceDesc: 'Livraisons ponctuelles et service client réactif'
      },
      payment: {
        title: 'Modes de paiement acceptés',
        subtitle: 'Pour votre confort, nous acceptons plusieurs moyens de paiement sécurisés',
        note: 'Paiement sécurisé • Facturation professionnelle disponible'
      },
      cta: {
        title: 'Une question ?',
        subtitle: 'Notre équipe est à votre disposition pour vous accompagner dans votre choix.',
        contact: 'Nous contacter'
      }
    },

    // Legal page
    legal: {
      title: 'Mentions légales',
      publisher: {
        title: 'Éditeur du site',
        company: 'Raison sociale :',
        address: 'Siège social :',
        email: 'Email :',
        phone: 'Téléphone :',
        registration: 'Numéro d\'identification :'
      },
      hosting: {
        title: 'Hébergement',
        content: 'Le site oceana.ci est hébergé par VENAME.'
      },
      intellectual: {
        title: 'Propriété intellectuelle',
        paragraph1: 'L\'ensemble des contenus présents sur le site oceana.ci (textes, images, graphismes, logo, icônes, etc.) est la propriété exclusive d\'Océana, à l\'exception des marques, logos ou contenus appartenant à d\'autres sociétés partenaires ou auteurs.',
        paragraph2: 'Toute reproduction, distribution, modification, adaptation, retransmission ou publication de ces différents éléments est strictement interdite sans l\'accord express par écrit d\'Océana.'
      },
      privacy: {
        title: 'Protection des données personnelles',
        paragraph1: 'Conformément à la réglementation applicable, Océana s\'engage à respecter la confidentialité des informations personnelles collectées sur ce site.',
        paragraph2: 'Les informations recueillies via les formulaires de contact sont destinées à Océana pour le traitement de vos demandes et la gestion de la relation client.',
        paragraph3: 'Vous disposez d\'un droit d\'accès, de rectification et de suppression des données vous concernant. Pour exercer ces droits, contactez-nous à : commandes@oceana-ci.com'
      },
      cookies: {
        title: 'Cookies',
        paragraph1: 'Le site oceana.ci peut être amené à utiliser des cookies pour améliorer l\'expérience utilisateur et réaliser des statistiques de visite.',
        paragraph2: 'Vous pouvez configurer votre navigateur pour refuser les cookies. Cependant, cela peut limiter certaines fonctionnalités du site.'
      },
      liability: {
        title: 'Limitation de responsabilité',
        paragraph1: 'Océana s\'efforce d\'assurer l\'exactitude et la mise à jour des informations diffusées sur ce site. Toutefois, Océana ne peut garantir l\'exactitude, la précision ou l\'exhaustivité des informations mises à disposition.',
        paragraph2: 'Océana ne pourra être tenue responsable des dommages directs ou indirects résultant de l\'accès au site ou de l\'utilisation de celui-ci.'
      },
      law: {
        title: 'Droit applicable',
        content: 'Les présentes mentions légales sont régies par le droit ivoirien. Tout litige relatif à l\'utilisation du site oceana.ci sera soumis à la compétence exclusive des tribunaux d\'Abidjan.'
      },
      lastUpdate: 'Dernière mise à jour :'
    },

    // FAQ component
    faq: {
      title: 'Questions fréquentes',
      subtitle: 'Trouvez rapidement les réponses à vos questions',
      q1: 'Quelles sont les zones de livraison ?',
      a1: 'Nous livrons dans toute la zone du Grand Abidjan : Cocody, Plateau, Marcory, Zone 4, Riviera, Bingerville et communes environnantes. Pour les livraisons en dehors de ces zones, contactez-nous.',
      q2: 'Quels sont les délais de livraison ?',
      a2: 'Pour les clients B2B, nous proposons des livraisons quotidiennes selon un planning établi. Pour les commandes boutique, le service pick-up est disponible sous 2h après confirmation.',
      q3: 'Comment devenir client professionnel ?',
      a3: 'Remplissez le formulaire sur notre page Distribution ou contactez-nous directement. Nous établirons un devis personnalisé et ouvrirons votre compte client sous 24h.',
      q4: 'Quels modes de paiement acceptez-vous ?',
      a4: 'Nous acceptons les paiements par Mobile Money, Bloom, Wave, carte bancaire et virement. Pour les clients B2B, nous proposons également la facturation mensuelle.'
    }
  },

  // English translations
  en: {
    // Navigation
    nav: {
      home: 'Home',
      distribution: 'B2B Distribution',
      production: 'Production',
      boutiques: "Océana Fresh",
      services: 'Enterprise Services',
      catalogue: 'Catalogue',
      about: 'About',
      contact: 'Contact',
      order: 'Order',
      proSpace: 'Pro Area',
      becomeClient: 'Become a client',
      investors: 'Investors'
    },
    
    // Common
    common: {
      learnMore: 'Learn more',
      contact: 'Contact us',
      send: 'Send',
      sending: 'Sending...',
      phone: 'Phone',
      email: 'Email',
      address: 'Address',
      hours: 'Hours',
      name: 'Name',
      company: 'Company',
      message: 'Message',
      submit: 'Submit',
      required: 'Required',
      optional: 'Optional',
      seeMore: 'See more',
      close: 'Close'
    },

    // Home page
    home: {
      hero: {
        title: 'Quality, freshness and reliability for your supplies',
        subtitle: 'Océana supports professionals and individuals with a premium range of fruits and vegetables, rigorous delivery service and solutions tailored to your needs.',
        ctaB2B: 'Become a B2B client',
        ctaOrder: 'Order now'
      },
      stats: {
        clients: 'Satisfied clients',
        experience: 'Years of experience',
        products: 'Premium products',
        satisfaction: 'Satisfaction rate'
      },
      trust: {
        title: 'They trust us',
        subtitle: 'Trusted supplier for the largest retail chains in Côte d\'Ivoire'
      },
      features: {
        freshness: 'Guaranteed freshness',
        freshnessDesc: 'Controlled cold chain and rigorous quality controls',
        delivery: 'Reliable delivery',
        deliveryDesc: 'Professional logistics and schedule compliance',
        sourcing: 'Premium sourcing',
        sourcingDesc: 'Rigorous selection of the best local and imported producers',
        traceability: 'Complete traceability',
        traceabilityDesc: 'Transparent tracking from origin to delivery'
      },
      boutique: {
        title: 'Océana Fresh Stores',
        subtitle: 'Our produce stores offer a premium shopping experience with a daily selection of top-quality fruits and vegetables.',
        feature1: 'Fresh juices pressed on-site',
        feature2: 'Customized gift baskets',
        feature3: 'Quick pick-up service',
        feature4: 'Meeting platters',
        cta: 'Discover our stores'
      },
      b2b: {
        title: 'Professional B2B Distribution',
        subtitle: 'Océana Distribution supplies hotels, restaurants, caterers and businesses with premium products and tailored service.',
        feature1: 'Dedicated client account with preferential rates',
        feature2: 'Scheduled regular deliveries',
        feature3: 'Wide range adapted to professionals',
        feature4: 'Monthly billing and personalized follow-up',
        cta: 'Request a quote'
      },
      testimonials: {
        title: 'What our clients say',
        subtitle: 'Customer satisfaction is our priority'
      },
      faq: {
        title: 'Frequently asked questions',
        subtitle: 'Find quick answers to your questions'
      },
      cta: {
        title: 'Ready to get started?',
        subtitle: 'Contact us to open a professional account or place your first order.',
        contact: 'Contact us'
      }
    },

    // Production page
    production: {
      hero: {
        badge: 'Océana Production S.A.',
        title: 'Production',
        subtitle: 'From owned farms to national farmer networks',
        tagline: 'Building a structured agricultural ecosystem across Côte d\'Ivoire',
        description: 'Océana is not just a producer — we are a fully integrated agricultural platform combining owned production and organized farmer networks.',
        ctaOwned: 'Owned production',
        ctaNetwork: 'Farmer network'
      },
      keyMessage: {
        equation: 'Océana = Owned production + Organized national farmer network',
        clarification: 'Not just a farm. Not just a trader. A structured agricultural platform.'
      },
      owned: {
        badge: 'Owned Production',
        title: 'Azaguié Site',
        description: 'Océana is developing its own production site located in Azaguié, which will serve as the central agricultural base for the group. This project represents our commitment to superior quality agriculture.',
        controlled: 'Controlled production',
        innovations: 'Pilot innovations',
        benchmark: 'Quality benchmark',
        functionsTitle: 'This site functions as:',
        demo: 'Demonstration farm',
        demoDesc: 'Showcase for best agricultural practices',
        rnd: 'R&D platform',
        rndDesc: 'Greenhouses, irrigation, yield optimization',
        anchor: 'Supply anchor',
        anchorDesc: 'Reliable source for our distribution network'
      },
      distributed: {
        badge: 'Distributed Production',
        title: 'Océana Food Hubs',
        subtitle: 'A national network of structured farmers',
        description: 'Beyond our owned farm, Océana is developing a network of regional agricultural clusters through partnerships with cooperatives and smallholder producers.',
        hubNorth: 'Northern Hub',
        hubNorthLocation: 'Korhogo Region',
        hubFuture: 'Future Hubs',
        hubFutureLocation: 'National expansion',
        active: 'Active',
        planned: 'In development',
        philosophy: 'Our philosophy',
        philosophyText1: 'Océana does not only farm land.',
        philosophyText2: 'Océana structures production.',
        organizedProducers: 'Organized producers',
        organizedProducersDesc: 'Supervised cooperatives and smallholders',
        centralPlanning: 'Central planning',
        centralPlanningDesc: 'Crop and volume coordination',
        qualityStandards: 'Quality standards',
        qualityStandardsDesc: 'Océana standards applied everywhere',
        aggregation: 'Aggregation',
        aggregationDesc: 'Collection and storage infrastructure'
      },
      howItWorks: {
        title: 'How the model works',
        subtitle: 'An integrated value chain, from farm to consumer',
        step1: 'Farmers',
        step1Desc: 'Contract production with technical support',
        step2: 'Production',
        step2Desc: 'Crops guided by Océana standards',
        step3: 'Aggregation',
        step3Desc: 'Collection and processing in regional hubs',
        step4: 'Distribution',
        step4Desc: 'Delivery to Abidjan and beyond',
        forProducers: 'For producers',
        forProducersFeature1: 'Secure production contracts',
        forProducersFeature2: 'Technical support and training',
        forProducersFeature3: 'Access to quality inputs and seeds',
        forClients: 'For clients',
        forClientsFeature1: 'Reliable and regular supply',
        forClientsFeature2: 'Complete product traceability',
        forClientsFeature3: 'Standardized and consistent quality'
      },
      infrastructure: {
        badge: 'Key Infrastructure',
        title: 'Sinématiali Pack House',
        description: 'A central component of our model is the pack house located in Sinématiali, serving the Northern hub. This infrastructure transforms dispersed production into a reliable supply chain.',
        collection: 'Collection',
        sorting: 'Sorting & Grading',
        packaging: 'Packaging',
        logistics: 'Logistics consolidation',
        quote: 'This infrastructure transforms dispersed production into a reliable and efficient supply chain.'
      },
      scale: {
        title: 'Scaling the network',
        subtitle: 'Our vision for Ivorian agriculture',
        phase1: 'Phase 1',
        phase1Title: 'Northern Hub (Korhogo)',
        phase1Desc: 'Operational. First structured agricultural cluster in the Korhogo region.',
        phase2: 'Phase 2',
        phase2Title: 'Regional expansion',
        phase2Desc: 'In progress. Extension to new production regions.',
        phase3: 'Phase 3',
        phase3Title: 'National network',
        phase3Desc: 'Vision. Nationwide coverage of Océana Food Hubs.',
        closingLine: 'From a single farm to a nationwide agricultural platform.'
      },
      contact: {
        title: 'Join the Océana ecosystem',
        subtitle: 'Whether you are a producer, cooperative, distributor or investor, discover how to collaborate with our integrated agricultural platform.',
        producers: 'Producers & Cooperatives',
        producersDesc: 'Join our structured production network',
        distributors: 'Distributors & Retailers',
        distributorsDesc: 'Access reliable and traceable supply',
        investors: 'Investors & Partners',
        investorsDesc: 'Participate in the development of Ivorian agriculture',
        formTitle: 'Contact us',
        organization: 'Organization',
        interest: 'Interest',
        interestProducer: 'Producer / Cooperative',
        interestDistributor: 'Distributor / Retailer',
        interestInvestor: 'Investor / Partner',
        interestOther: 'Other'
      },
      cta: {
        title: 'Discover all our activities',
        subtitle: 'Production, distribution and stores — Océana covers the entire agricultural value chain.',
        distribution: 'B2B Distribution',
        boutiques: 'Our stores'
      }
    },

    // Contact page
    contact: {
      title: 'Contact us',
      subtitle: 'Our team is available to answer your questions',
      form: {
        name: 'Full name',
        email: 'Email',
        phone: 'Phone',
        subject: 'Subject',
        subjectOptions: {
          b2b: 'B2B Request / Professional account',
          order: 'Store order',
          service: 'Enterprise services',
          info: 'General information',
          other: 'Other'
        },
        message: 'Your message',
        send: 'Send message',
        success: 'Message sent',
        successDesc: 'We will get back to you shortly.'
      },
      info: {
        address: 'Our address',
        hours: 'Opening hours',
        weekdays: 'Monday - Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
        closed: 'Closed'
      }
    },

    // Footer
    footer: {
      description: 'Your trusted partner for premium fruit and vegetable supply in Côte d\'Ivoire.',
      quickLinks: 'Quick links',
      contact: 'Contact',
      paymentMethods: 'Payment methods',
      copyright: 'All rights reserved.',
      legal: 'Legal notice',
      privacy: 'Privacy policy'
    },

    // Promo banner
    promo: {
      offer: 'Special B2B offer:',
      discount: '-10% on your first order with code'
    },

    // Trust badges
    badges: {
      quality: 'Quality guaranteed',
      delivery: 'Fast delivery',
      service: '7 days a week',
      fresh: 'Fresh products',
      experience: '10 years experience',
      satisfaction: '98% satisfied'
    },

    // Distribution page
    distribution: {
      hero: {
        title: 'Professional B2B Distribution',
        subtitle: 'Océana Distribution is your reliable partner for premium fruit and vegetable supply. We serve hotels, restaurants, caterers, offices and residences with rigor and regularity.'
      },
      clients: {
        title: 'Our reference clients',
        subtitle: 'We supply the largest retail chains in Côte d\'Ivoire'
      },
      whoWeServe: {
        title: 'Who we serve',
        hotels: 'Hotels',
        hotelsDesc: 'Daily supply for your kitchens and buffets',
        restaurants: 'Restaurants',
        restaurantsDesc: 'Fresh, seasonal products for your menus',
        companies: 'Companies',
        companiesDesc: 'Baskets and services for your offices',
        caterers: 'Caterers',
        caterersDesc: 'Volume and quality for your events'
      },
      howItWorks: {
        title: 'How it works',
        step1: 'Quote request',
        step1Desc: 'Fill out the form or contact us directly to present your needs',
        step2: 'Account opening',
        step2Desc: 'We set up your client account with preferential rates and adapted conditions',
        step3: 'Regular delivery',
        step3Desc: 'Easy orders and punctual deliveries according to your schedule'
      },
      products: {
        title: 'Our professional range'
      },
      quality: {
        title: 'Our quality commitment',
        coldChain: 'Cold chain',
        coldChainDesc: 'Refrigerated trucks and climate-controlled warehouses for optimal freshness',
        controls: 'Quality controls',
        controlsDesc: 'Systematic sorting and visual inspection before each delivery',
        traceability: 'Traceability',
        traceabilityDesc: 'Complete tracking from origin to delivery with documentation'
      },
      farm: {
        badge: 'Océana Production',
        title: 'From farm to plate',
        tagline: 'Grown here, delivered tomorrow.',
        description: 'Océana Production is developing a project of 30,000 m² of hydroponic greenhouses in Azaguié to supply supermarkets in Abidjan with fresh, traceable, locally produced vegetables.',
        stats: {
          greenhouses: 'of greenhouses planned',
          tons: 'tons/year (target)',
          families: 'product families',
          pesticides: 'pesticides',
          delivery: 'D+1 delivery'
        },
        productsTitle: 'Our future products',
        productsSubtitle: '5 families of premium vegetables planned, to be grown in greenhouses and harvested at maturity',
        tomatoes: 'Tomatoes',
        tomatoesDesc: 'Cherry & round',
        cucumbers: 'Cucumbers',
        cucumbersDesc: 'Fresh and crunchy',
        peppers: 'Peppers',
        peppersDesc: 'Red, yellow & green',
        lettuce: 'Lettuce',
        lettuceDesc: 'Fresh leaves',
        herbs: 'Fresh herbs',
        herbsDesc: 'Basil, mint...',
        differenceTitle: 'Our difference',
        betterTaste: 'Better taste, longer shelf life',
        betterTasteDesc: 'Unlike imported products from Morocco or Europe, our vegetables are harvested at maturity, packaged the same day and delivered under cold chain directly to fresh produce sections.',
        harvest: 'Harvest at optimal maturity',
        packaging: 'Same-day packaging',
        coldChainGuaranteed: 'Guaranteed cold chain',
        traceabilityFull: 'Traceability from plant to packaging',
        partnerCta: 'Become a distribution partner',
        partnerDesc: 'Integrate our local and premium vegetables into your fresh produce sections. We support supermarkets, wholesalers and distributors.',
        partnerBtn: 'Become a distribution partner',
        learnMore: 'Learn more about the project'
      },
      form: {
        title: 'Open a professional account',
        subtitle: 'Fill out this form and we will contact you within 24 hours to set up your client account.',
        paymentTitle: 'Accepted payment methods',
        paymentNote: 'Monthly billing • Flexible payment terms',
        companyName: 'Company name',
        contactName: 'Contact name',
        email: 'Email',
        phone: 'Phone',
        businessType: 'Business type',
        selectOption: 'Select...',
        hotel: 'Hotel',
        restaurant: 'Restaurant',
        caterer: 'Caterer',
        company: 'Company',
        residence: 'Residence',
        other: 'Other',
        volumeTitle: 'Needs and estimated volumes',
        volumeOptional: 'Estimated volume (optional)',
        volumeSmall: 'Small (less than 50kg/week)',
        volumeMedium: 'Medium (50-200kg/week)',
        volumeLarge: 'Large (200-500kg/week)',
        volumeVeryLarge: 'Very large (more than 500kg/week)',
        messagePlaceholder: 'Describe your specific needs...',
        submit: 'Send request',
        sending: 'Sending...',
        success: 'Request sent ✓',
        successDesc: 'We will contact you within 24 hours to discuss your needs.',
        error: 'Error',
        errorDesc: 'An error occurred. Please try again.',
        whatsappNote: 'Or contact us directly on'
      }
    },

    // Boutiques page
    boutiques: {
      hero: {
        title: "Océana Fresh",
        subtitle: "Our produce stores offer a premium shopping experience with a daily selection of top-quality fruits and vegetables, freshly pressed juices and personalized services."
      },
      experience: {
        title: "A premium shopping experience",
        subtitle: "Every morning, our teams select the best products to offer you exceptional freshness and quality.",
        dailySelection: 'Daily selection',
        dailySelectionDesc: 'Fresh products renewed every day',
        personalAdvice: 'Personal advice',
        personalAdviceDesc: 'Experienced team at your service',
        modernAmbiance: 'Modern ambiance',
        modernAmbianceDesc: 'Clean and well-organized spaces'
      },
      services: {
        title: 'Our services',
        fruitBar: 'Fruit Bar & Juices',
        fruitBarDesc: '100% natural juices pressed on demand. Oranges, pineapples, watermelon, carrots, and custom blends.',
        baskets: 'Custom baskets',
        basketsDesc: 'Made-to-measure compositions for your offices, corporate gifts or events.',
        platters: 'Meeting platters',
        plattersDesc: 'Cut and presented fruits for your meetings and gourmet breaks.',
        pickup: 'Pick-up service',
        pickupDesc: 'Order via WhatsApp and pick up your order ready in store.'
      },
      locations: {
        title: 'Our stores',
        servicesAvailable: 'Available services:'
      },
      cta: {
        title: 'Order or reserve',
        subtitle: 'Contact us on WhatsApp to place an order, reserve your juices or order your baskets.',
        button: 'Order on WhatsApp'
      }
    },

    // Services page
    services: {
      hero: {
        title: 'Enterprise Services',
        subtitle: 'Adapted solutions to improve the well-being of your teams and the quality of your meetings. Regular or one-time delivery according to your needs.'
      },
      whyChoose: {
        title: 'Why choose our services',
        flexibility: 'Flexibility',
        flexibilityDesc: 'One-time or recurring deliveries, adapted to your calendar',
        quality: 'Consistent quality',
        qualityDesc: 'Rigorous selection and careful presentation with each delivery',
        personalized: 'Personalized service',
        personalizedDesc: 'Custom compositions according to your preferences and constraints'
      },
      howToOrder: {
        title: 'How to order',
        subtitle: 'Contact us to discuss your needs. We will establish together a personalized offer with rates and delivery schedule.',
        whatsapp: 'Order on WhatsApp',
        email: 'Send an email'
      },
      testimonials: {
        title: 'What our clients say',
        testimonial1: 'The weekly fruit baskets have become an anticipated event by the whole team. Quality and freshness at their best.',
        testimonial1Role: 'Office Manager',
        testimonial2: 'Professional and punctual service. The platters for our meetings are always presented with care.',
        testimonial2Role: 'F&B Manager'
      }
    },

    // Catalogue page
    catalogue: {
      hero: {
        title: 'Our catalogue',
        subtitle: 'A varied selection of fresh fruits and vegetables, local and imported. Availability varies according to seasons and daily arrivals.'
      },
      categories: {
        title: 'Product families',
        askList: 'Ask for today\'s list'
      },
      availability: {
        title: 'Availability and freshness',
        subtitle: 'Our catalogue evolves daily according to arrivals and seasonality. To know the complete list and today\'s prices, contact us directly.',
        local: 'Local products',
        localDesc: 'Direct sourcing from selected Ivorian producers',
        imported: 'Imported products',
        importedDesc: 'International selection via network of trusted importers',
        premium: 'Premium products',
        premiumDesc: 'Exceptional pieces and rare varieties according to availability'
      },
      cta: {
        title: 'Need information?',
        subtitle: 'Contact us to receive the complete list of available products, prices and place an order.',
        button: 'Contact on WhatsApp'
      }
    },

    // About page
    about: {
      hero: {
        title: 'About Océana',
        subtitle: 'Premium player in the fruit & vegetable chain in Abidjan, designed to serve professionals and individuals with rigor and quality.'
      },
      history: {
        title: 'Our history',
        paragraph1: 'Océana was born from the conviction that it was possible to reconcile premium quality, rigorous service and accessibility in the fruit and vegetable sector in Abidjan.',
        paragraph2: 'We have built our reputation on the reliability of our B2B supplies and the freshness of our produce stores. Today, we serve the largest hotels, restaurants and companies in the area, while welcoming a demanding clientele in our "Océana Fresh" stores.'
      },
      mission: {
        title: 'Mission & Values',
        excellence: 'Excellence',
        excellenceDesc: 'Rigor in selection, storage and delivery of each product',
        reliability: 'Reliability',
        reliabilityDesc: 'Commitment honored, schedules respected, consistent quality',
        passion: 'Passion',
        passionDesc: 'Love of good products and customer satisfaction'
      },
      coverage: {
        title: 'Areas covered',
        subtitle: 'We deliver throughout the Greater Abidjan area with optimized logistics to ensure freshness and punctuality.',
        zone1: 'Cocody, Plateau, Marcory',
        zone2: 'Yopougon, Adjamé, Treichville',
        zone3: 'Bingerville, Abobo, Port-Bouët'
      },
      sourcing: {
        title: 'Sourcing',
        subtitle: 'Our dual local and international approach allows us to offer quality and diversity.',
        local: 'Local producers',
        localDesc: 'Direct partnerships with Ivorian producers selected for their expertise',
        network: 'Importer network',
        networkDesc: 'International supplies via reliable and certified channels'
      },
      commitments: {
        title: 'Our operational commitments',
        coldChain: 'Cold chain',
        coldChainDesc: 'Refrigerated trucks and climate-controlled warehouses',
        traceability: 'Traceability',
        traceabilityDesc: 'Complete tracking from origin to delivery',
        qualityControl: 'Quality control',
        qualityControlDesc: 'Systematic sorting and visual inspection',
        reliableDelivery: 'Reliable delivery',
        reliableDeliveryDesc: 'Schedule compliance and commitment honored'
      }
    },

    // Become client page
    becomeClient: {
      hero: {
        title: 'Become an Océana client',
        subtitle: 'Whether you are a professional or an individual, Océana supports you with solutions adapted to your needs.'
      },
      professionals: {
        title: 'Professionals',
        subtitle: 'Are you a hotel, restaurant, caterer, company or residence? Open your professional account.',
        feature1: 'Dedicated client account with preferential rates',
        feature2: 'Scheduled regular deliveries',
        feature3: 'Wide range adapted to professionals',
        feature4: 'Personalized monthly billing',
        feature5: 'Dedicated customer service',
        ctaPrimary: 'Open a pro account',
        ctaSecondary: 'Contact on WhatsApp'
      },
      individuals: {
        title: 'Individuals',
        subtitle: 'Discover our "Océana Fresh" stores for a premium shopping experience.',
        feature1: 'Daily selection of fresh products',
        feature2: 'Fresh juices pressed on site',
        feature3: 'Personalized baskets',
        feature4: 'Quick pick-up service',
        feature5: 'Order via WhatsApp',
        ctaPrimary: 'Discover our stores',
        ctaSecondary: 'Order on WhatsApp'
      },
      enterpriseServices: {
        title: 'Enterprise services',
        subtitle: 'Are you a company and want to offer fruit baskets to your teams or platters for your meetings?',
        cta: 'Discover our services'
      },
      whyChoose: {
        title: 'Why choose Océana',
        premiumQuality: 'Premium quality',
        premiumQualityDesc: 'Rigorous selection of the best local and imported products',
        freshness: 'Guaranteed freshness',
        freshnessDesc: 'Controlled cold chain and rigorous quality controls',
        reliableService: 'Reliable service',
        reliableServiceDesc: 'Punctual deliveries and responsive customer service'
      },
      payment: {
        title: 'Accepted payment methods',
        subtitle: 'For your convenience, we accept several secure payment methods',
        note: 'Secure payment • Professional billing available'
      },
      cta: {
        title: 'Any questions?',
        subtitle: 'Our team is at your disposal to help you with your choice.',
        contact: 'Contact us'
      }
    },

    // Legal page
    legal: {
      title: 'Legal notice',
      publisher: {
        title: 'Site publisher',
        company: 'Company name:',
        address: 'Headquarters:',
        email: 'Email:',
        phone: 'Phone:',
        registration: 'Registration number:'
      },
      hosting: {
        title: 'Hosting',
        content: 'The oceana.ci website is hosted by VENAME.'
      },
      intellectual: {
        title: 'Intellectual property',
        paragraph1: 'All content on the oceana.ci website (texts, images, graphics, logo, icons, etc.) is the exclusive property of Océana, with the exception of trademarks, logos or content belonging to other partner companies or authors.',
        paragraph2: 'Any reproduction, distribution, modification, adaptation, retransmission or publication of these elements is strictly prohibited without the express written consent of Océana.'
      },
      privacy: {
        title: 'Personal data protection',
        paragraph1: 'In accordance with applicable regulations, Océana undertakes to respect the confidentiality of personal information collected on this site.',
        paragraph2: 'Information collected via contact forms is intended for Océana for processing your requests and managing customer relations.',
        paragraph3: 'You have the right to access, rectify and delete data concerning you. To exercise these rights, contact us at: commandes@oceana-ci.com'
      },
      cookies: {
        title: 'Cookies',
        paragraph1: 'The oceana.ci website may use cookies to improve the user experience and produce visit statistics.',
        paragraph2: 'You can configure your browser to refuse cookies. However, this may limit certain features of the site.'
      },
      liability: {
        title: 'Limitation of liability',
        paragraph1: 'Océana strives to ensure the accuracy and updating of information published on this site. However, Océana cannot guarantee the accuracy, precision or completeness of the information made available.',
        paragraph2: 'Océana cannot be held responsible for direct or indirect damage resulting from access to the site or its use.'
      },
      law: {
        title: 'Applicable law',
        content: 'These legal notices are governed by Ivorian law. Any dispute relating to the use of the oceana.ci website will be subject to the exclusive jurisdiction of the courts of Abidjan.'
      },
      lastUpdate: 'Last update:'
    },

    // FAQ component
    faq: {
      title: 'Frequently asked questions',
      subtitle: 'Find quick answers to your questions',
      q1: 'What are the delivery areas?',
      a1: 'We deliver throughout the Greater Abidjan area: Cocody, Plateau, Marcory, Zone 4, Riviera, Bingerville and surrounding municipalities. For deliveries outside these areas, contact us.',
      q2: 'What are the delivery times?',
      a2: 'For B2B clients, we offer daily deliveries according to an established schedule. For store orders, pick-up service is available within 2 hours after confirmation.',
      q3: 'How to become a professional client?',
      a3: 'Fill out the form on our Distribution page or contact us directly. We will establish a personalized quote and open your client account within 24 hours.',
      q4: 'What payment methods do you accept?',
      a4: 'We accept payments by Mobile Money, Bloom, Wave, credit card and bank transfer. For B2B clients, we also offer monthly billing.'
    }
  }
};

export default LanguageProvider;
